import { Dialog } from '@hai/ui-react';
import { IHaiDialogProps } from '@hai/ui-react/dist/types';
import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { notificationHandler } from 'src/js/notification/notification-handler';

import { StillImage } from './still-image-model';

const CANCEL_BUTTON_ID = 'stillimage-delete-confirm-modal-cancel';
const SUBMIT_BUTTON_ID = 'stillimage-delete-confirm-modal-submit';

interface Props {
  itemToDelete: StillImage;
  onCancel?: VoidFunction;
  onApply: VoidFunction;
}

export const DeleteStillImageConfirmModal: React.FunctionComponent<Props> = ({ onApply, onCancel, itemToDelete }) => {
  const { t } = useTranslation();
  const keyDownTargetRef = useRef<HTMLElement | null>(null);

  const handleDelete = () => {
    const path = APIPathWithArguments(APIPath.stillimages.single, { id: itemToDelete.name });
    return apiClientWithoutHandler.genericController
      .delete(path, itemToDelete)
      .then((res: any) => {
        notificationHandler(
          res,
          t('streaming.stillImages.notifications.deleteSuccessMsg'),
          t('streaming.stillImages.notifications.deleteErrorMsg'),
          t,
        );
        return res;
      })
      .then((res: any) => {
        if (res.ok) {
          onApply();
        }
      });
  };

  const handleOnKeyDown = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      keyDownTargetRef.current = e.target as HTMLElement;
    }
  };

  const handleOnKeyUp = (e: React.KeyboardEvent<HTMLElement>): void => {
    if (e.key === 'Enter') {
      const keyUpTarget = e.target as HTMLElement;
      if (keyDownTargetRef.current !== keyUpTarget) {
        keyDownTargetRef.current = null;
        return;
      }

      keyDownTargetRef.current = null;

      e.stopPropagation();
      e.preventDefault();
      e.nativeEvent.stopImmediatePropagation();

      if (keyUpTarget.id === CANCEL_BUTTON_ID) {
        onCancel?.();
        return;
      }

      if (keyUpTarget.id === SUBMIT_BUTTON_ID) {
        return;
      }
    }
  };

  useLayoutEffect(() => {
    const el = document.getElementById(CANCEL_BUTTON_ID);
    if (el) {
      el.focus();
      el.classList.add('force-focus-visible');
    }

    return () => {
      el?.classList.remove('force-focus-visible');
    };
  }, []);

  return (
    <Dialog
      headline={t('streaming.stillImages.delete.title')}
      bodyText={t('general.cannotUndo')}
      onClose={onCancel}
      {...({ onKeyUp: handleOnKeyUp } as IHaiDialogProps)}
      {...({ onKeyDown: handleOnKeyDown } as IHaiDialogProps)}
      show={true}
      buttons={[
        { label: t('general.cancel'), id: CANCEL_BUTTON_ID, onClick: onCancel },
        { label: t('general.delete'), variant: 'danger', onClick: handleDelete, id: SUBMIT_BUTTON_ID },
      ]}
    />
  );
};
